import { IStep } from '../../../../../../components/VerticalStepper/VerticalStepper';
import { STEPS_STYLE_NAMES, STEPS_STYLES } from '../../../../../../components/VerticalStepper/VerticalStepper.types';
import { ORDER_STATUS_SEQUENCE } from '../../../../../../constants/history.constants';
import { IOrderHistory, IOrderHistoryEvent } from '../../../../../../types/suscription/history.types';
import { mapEventsToSubSteps } from '../../../../../../utils/history';
import { ORDER_STATUS } from '../../../../../../types/orders';

/**
 * Nombres visibles de los estados de la orden
 */
export const ORDER_STATUS_VISIBLE_NAMES = {
  [ORDER_STATUS.PENDING]: 'Por confirmar',
  [ORDER_STATUS.ACTIVE]: 'Activo',
  [ORDER_STATUS.DELIVERED]: 'Entregado',
  [ORDER_STATUS.CANCELLED]: 'Cancelado'
};

interface IStatusStyles {
  titleColor: string;
  icon: React.ReactNode;
  isOpaque: boolean;
}

/**
 * Determina los estilos de cada paso del VerticalStepper para cada estado del historial de la orden
 * @param targetStatus - Estado que se está evaluando
 * @param currentStatus - Estado actual de la orden
 * @returns Objeto con el componente de icono correspondiente, el color del titulo y si el contenido del paso es opaco
 */
export const determineOrderHistoryStepStyles = (
  targetStatus: ORDER_STATUS,
  currentStatus: ORDER_STATUS
): IStatusStyles => {
  if (currentStatus === ORDER_STATUS.CANCELLED && targetStatus === ORDER_STATUS.CANCELLED) {
    return STEPS_STYLES[STEPS_STYLE_NAMES.CANCELLED];
  }

  if (currentStatus === ORDER_STATUS.CANCELLED && targetStatus !== ORDER_STATUS.CANCELLED) {
    return STEPS_STYLES[STEPS_STYLE_NAMES.SECONDARY_CANCELLED];
  }

  if (targetStatus === ORDER_STATUS.DELIVERED && currentStatus === ORDER_STATUS.DELIVERED) {
    return STEPS_STYLES[STEPS_STYLE_NAMES.SUCCESS];
  }

  const targetStatusIndex = ORDER_STATUS_SEQUENCE.indexOf(targetStatus);
  const orderStatusIndex = ORDER_STATUS_SEQUENCE.indexOf(currentStatus);

  if (targetStatusIndex < orderStatusIndex) return STEPS_STYLES[STEPS_STYLE_NAMES.SUCCESS];
  if (targetStatusIndex === orderStatusIndex) return STEPS_STYLES[STEPS_STYLE_NAMES.IN_PROGRESS];

  return STEPS_STYLES[STEPS_STYLE_NAMES.WAITING];
};

/**
 * Obtiene los eventos del historial de la orden para un estado específico
 * @param history - Historial de la orden
 * @param statusName - Estado que se está evaluando
 * @returns Los eventos para el estado específico
 */
export const getEventsForStatus = (history: IOrderHistory[], statusName: ORDER_STATUS): IOrderHistoryEvent[] => {
  return history.find(order => order.name === statusName)?.events || [];
};

/**
 * Transforma el historial de la orden a pasos del componente VerticalStepper
 * @param history - Historial de la orden
 * @param currentStatus - Estado actual de la orden
 * @returns Los pasos del historial de la orden transformados a pasos del componente VerticalStepper
 */
export const transformOrderHistoryToStepperSteps = (history: IOrderHistory[], currentStatus: ORDER_STATUS): IStep[] => {
  if (history.length === 0) {
    return [];
  }

  const steps = ORDER_STATUS_SEQUENCE.map(status => {
    const { titleColor, icon, isOpaque } = determineOrderHistoryStepStyles(status, currentStatus);
    const subSteps = mapEventsToSubSteps(getEventsForStatus(history, status));

    return {
      title: ORDER_STATUS_VISIBLE_NAMES[status],
      titleColor,
      icon,
      subSteps,
      isOpaque
    };
  });

  if (currentStatus === ORDER_STATUS.CANCELLED) {
    const stepsWithSubSteps = steps.filter(step => step.subSteps && step.subSteps.length > 0);

    const { titleColor, icon, isOpaque } = determineOrderHistoryStepStyles(currentStatus, currentStatus);
    const subSteps = mapEventsToSubSteps(getEventsForStatus(history, currentStatus));

    const finalStepsWithCancelStep = stepsWithSubSteps.concat({
      title: ORDER_STATUS_VISIBLE_NAMES[currentStatus],
      titleColor,
      icon,
      subSteps,
      isOpaque
    });

    return finalStepsWithCancelStep;
  }

  return steps;
};
